import { register } from "register-service-worker";
import { offerUpdate, handleControllerChange } from './utils/pwa/updates';

const APP_SHELL_CHECK_INTERVAL_MS = 5 * 60 * 1000;
let hasActiveController = Boolean(navigator.serviceWorker?.controller);

const unregisterLegacyServiceWorkers = async () => {
  if (!("serviceWorker" in navigator)) {
    return;
  }

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(
      registrations.map((registration) => registration.unregister()),
    );

    if ("caches" in window) {
      const cacheKeys = await window.caches.keys();
      await Promise.all(
        cacheKeys.map((cacheKey) => window.caches.delete(cacheKey)),
      );
    }
  } catch (error) {
    console.error("Error clearing service workers:", error);
  }
};

const normalizeAssetUrl = (value) => {
  try {
    const url = new URL(value, window.location.href);

    if (url.origin !== window.location.origin) {
      return "";
    }

    return `${url.pathname}${url.search}`;
  } catch (error) {
    return "";
  }
};

const uniqueSortedSignature = (values) =>
  Array.from(new Set(values.filter(Boolean))).sort().join("|");

const currentAssetSignature = () =>
  uniqueSortedSignature(
    Array.from(document.querySelectorAll('script[src],link[rel="stylesheet"][href]'))
      .map((node) => normalizeAssetUrl(node.src || node.href)),
  );

// A build ID is independent of when lazy route styles enter the document.
const initialAppShellSignature = process.env.VUE_APP_BUILD_STAMP || currentAssetSignature();

const assetSignatureFromHtml = (html) => {
  const buildId = html.match(/<meta\s+name=["']app-build["']\s+content=["']([^"']+)["']/i)?.[1];
  if (buildId) return buildId;
  const assets = [];
  const pattern = /\b(?:src|href)="([^"]+\.(?:js|css)(?:\?[^"]*)?)"/g;
  let match;

  while ((match = pattern.exec(html)) !== null) {
    assets.push(normalizeAssetUrl(match[1]));
  }

  return uniqueSortedSignature(assets);
};

const appShellUrl = () => {
  const baseUrl = new URL(process.env.BASE_URL || "/", window.location.origin);
  baseUrl.pathname = `${baseUrl.pathname.replace(/\/?$/, "/")}index.html`;
  baseUrl.searchParams.set("_fresh", `${Date.now()}-${Math.random().toString(16).slice(2)}`);
  return baseUrl.toString();
};

const refreshIfAppShellChanged = async () => {
  const response = await fetch(appShellUrl(), {
    cache: "no-store",
    headers: {
      "Cache-Control": "no-store, no-cache, must-revalidate, max-age=0",
      Pragma: "no-cache",
    },
  });

  if (!response.ok) {
    return;
  }

  const currentSignature = initialAppShellSignature;
  const nextSignature = assetSignatureFromHtml(await response.text());

  if (currentSignature && nextSignature && currentSignature !== nextSignature) {
    offerUpdate({ signature: nextSignature });
  }
};

const scheduleAppShellFreshnessChecks = () => {
  let running = false;
  const check = async () => {
    if (document.visibilityState === "hidden" || navigator.onLine === false || running) {
      return;
    }

    running = true;
    try {
      await refreshIfAppShellChanged();
    } catch (error) {
      // A transient network error must not block the ERP.
    } finally {
      running = false;
    }
  };

  window.setTimeout(check, 15000);
  window.setInterval(check, APP_SHELL_CHECK_INTERVAL_MS);
  window.addEventListener("focus", check);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      check();
    }
  });
};

const onServiceWorkerControllerChange = () => {
  if (!hasActiveController) {
    hasActiveController = true;
    return;
  }
  handleControllerChange();
};

const checkWorkerUpdate = (registration) => {
  try {
    if (registration?.active && typeof registration.update === "function") {
      registration.update().catch(() => {});
    }

    if (registration?.active && registration.waiting) {
      offerUpdate({ registration });
    }
  } catch (error) {
    // Service worker update checks are best-effort and must never stop startup.
  }
};

const isProduction = process.env.NODE_ENV === "production";
const shouldRegisterServiceWorkerCleanup = isProduction;

if (!shouldRegisterServiceWorkerCleanup) {
  unregisterLegacyServiceWorkers();
}

if (shouldRegisterServiceWorkerCleanup) {
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.addEventListener(
      "controllerchange",
      onServiceWorkerControllerChange,
    );
  }

  scheduleAppShellFreshnessChecks();

  register(`${process.env.BASE_URL}service-worker.js`, {
    registrationOptions: {
      scope: process.env.BASE_URL,
      updateViaCache: "none",
    },
    ready(registration) {
      checkWorkerUpdate(registration);
      // console.log(
      //   'App is being served from cache by a service worker.\n' +
      //   'For more details, visit https://goo.gl/AFskqB'
      // )
    },
    registered(registration) {
      checkWorkerUpdate(registration);
      // console.log('Service worker has been registered.')
    },
    cached() {
      // console.log('Content has been cached for offline use.')
    },
    updatefound() {},
    updated(registration) {
      if (registration?.active && registration.waiting) offerUpdate({ registration });
    },
    offline() {
      // Este sistema opera 100% online.
    },
    error(error) {
      console.error("Error during service worker registration:", error);
    },
  });
}
